
// What a revert does, for the confirmation and the details footer. Only the latest change to a set of
// data can be taken back, so a revert never has anything newer to overwrite.
export const revertParagraph = (): string =>
  "Të dhënat kthehen saktësisht siç ishin para këtij ndryshimi. " +
  "Ky kthim regjistrohet vetë në historik.";

// Why an older change is not offered: how many changes came after it decides the wording.
const supersededSentence = (newerChanges: number): string => {
  const after =
    newerChanges === 1
      ? "pas tij është bërë edhe një ndryshim tjetër"
      : `pas tij janë bërë edhe ${newerChanges} ndryshime të tjera`;
  return (
    `Ky nuk është ndryshimi i fundit mbi këto të dhëna: ${after}. Kthimi do t'i zhbënte edhe ato ` +
    "pa pyetur, prandaj kthehet vetëm ndryshimi i fundit."
  );
};

// Why one change cannot be undone, by the code the backend sends. The first two are about what
// the data is; the rest about this one row. The backend also sends an English sentence — it is the
// fallback, never what a reader should see first.
const BLOCKED_SENTENCES: Record<string, string> = {
  "money-or-provider":
    "Ky ndryshim nuk kthehet mbrapa nga këtu: ai vetëm regjistron diçka që ka ndodhur jashtë sistemit " +
    "tonë — para që kanë lëvizur, një porosi te furnitori ose një dokument i dërguar. Kthimi i vlerave " +
    "të vjetra nuk e zhbën atë, prandaj korrigjimi duhet bërë aty ku u krye veprimi.",
  "customer-data":
    "Këto të dhëna i përkasin një personi. Kthimi mbrapa mund të rikthejë diçka që personi ka kërkuar " +
    "të hiqet, prandaj nuk lejohet nga këtu.",
  "not-classified":
    "Ende nuk është vendosur nëse ky lloj të dhënash mund të kthehet mbrapa pa pasoja, prandaj kthimi " +
    "nuk ofrohet.",
  "unknown-type": "Ky lloj të dhënash nuk njihet më nga sistemi, prandaj kthimi nuk është i mundur.",
  "replaced-identical":
    "Ky rresht zëvendësoi një të njëjtë brenda së njëjtës ruajtje; s'ka çfarë të kthehet.",
  "replaced-in-same-save":
    "Ky rresht u zëvendësua brenda së njëjtës ruajtje; ktheni rreshtin që e zëvendësoi.",
  "hard-deleted": "Këto të dhëna u fshinë përfundimisht dhe nuk mund të rikthehen me numrin e vjetër.",
  "no-previous-state":
    "Për këtë ndryshim nuk është ruajtur gjendja e mëparshme, prandaj nuk ka ku të kthehet.",
  "already-reverted":
    "Ky ndryshim është kthyer tashmë mbrapa. Ajo që vlen tani është kthimi, dhe ai ka rreshtin e vet " +
    "në historik."
};

// The same for a whole action: what the reader is refused is the button on the action, not on a row.
const ACTION_BLOCKED_SENTENCES: Record<string, string> = {
  "money-or-provider":
    "Ky veprim nuk kthehet mbrapa: ai preku edhe të dhëna që regjistrojnë diçka jashtë sistemit tonë " +
    "(një rezervim, një pagesë, një faturë ose një eSIM). Kthimi vetëm i pjesës tjetër do të linte të " +
    "dhënat në një gjendje që veprimi nuk e ka krijuar kurrë.",
  "customer-data":
    "Ky veprim nuk kthehet mbrapa: ai preku edhe të dhëna që i përkasin një personi.",
  "not-classified":
    "Ky veprim nuk kthehet mbrapa: për një pjesë të të dhënave që preku ende nuk është vendosur nëse " +
    "kthimi është i sigurt.",
  "already-reverted": "Ky veprim është kthyer tashmë mbrapa."
};

// What the undo does not reach. The change is undone here; the copy someone outside reads follows later.
const WARNING_SENTENCES: Record<string, string> = {
  "gtfs-feed":
    "Te ne ndryshimi kthehet menjëherë, por skedari GTFS që marrin partnerët rifreskohet kur të punojë " +
    "procesi i radhës.",
  "published-content": "Faqja publike dhe kërkimi mund ta tregojnë tekstin e vjetër edhe për pak kohë.",
  "stored-file": "Kthimi i rikthen të dhënat, por jo skedarin e fshirë nga ruajtja."
};

// The sentence for a refused change; the backend's English text only if we have no sentence for the code.
export const revertBlockedSentence = (
  code: string | null,
  fallback: string | null,
  newerChanges = 0
): string => {
  if (code === "superseded" && newerChanges > 0) return supersededSentence(newerChanges);
  return (code && BLOCKED_SENTENCES[code]) || fallback || "Ky ndryshim nuk mund të kthehet mbrapa.";
};

// The same for a whole action.
export const actionBlockedSentence = (code: string | null, fallback: string | null): string =>
  (code && (ACTION_BLOCKED_SENTENCES[code] ?? BLOCKED_SENTENCES[code])) ||
  fallback ||
  "Ky veprim nuk mund të kthehet mbrapa.";

// What to add to a confirmation when the undo leaves something outside untouched; empty when nothing.
export const revertWarningSentence = (code: string | null): string =>
  (code && WARNING_SENTENCES[code]) || "";
